import React, { useState } from 'react';
import {
  Box, Container, VStack, Heading, Text, FormControl, FormLabel,
  Input, Button, Card, CardBody, CardHeader, Divider, InputGroup,
  InputRightElement, Icon, HStack, Flex, Badge,
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerBody,
  useDisclosure, useBreakpointValue, IconButton
} from '@chakra-ui/react';
import { LockIcon, ViewIcon, ViewOffIcon, EmailIcon, HamburgerIcon } from '@chakra-ui/icons';
import { ModernSidebar, UserMenu } from '../Main/SharedComponents';
import toastService from '../../utils/toastService';
import API_URL from '../../config';

const Settings = ({ state, onRouteChange, onEmailChange, toggleSidebar, getOverview, onRandomChapter }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toastService.error('Missing Fields', 'Please fill in all password fields.');
      return;
    }

    if (newPassword.length < 8) {
      toastService.error('Invalid Password', 'New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toastService.error("Passwords Don't Match", 'New passwords must match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: state.username,
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        toastService.success('Password Changed', data.message);
        // Clear form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toastService.error('Change Failed', data.message);
      }
    } catch (error) {
      toastService.networkError('Request Failed', 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !confirmEmail) {
      toastService.error('Missing Fields', 'Please fill in both email fields.');
      return;
    }

    if (newEmail !== confirmEmail) {
      toastService.error("Emails Don't Match", 'Both email fields must match.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toastService.error('Invalid Email', 'Please enter a valid email address.');
      return;
    }

    setEmailLoading(true);
    try {
      const response = await fetch(`${API_URL}/change_email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: state.username,
          new_email: newEmail
        })
      });

      const data = await response.json();

      if (data.success) {
        toastService.success('Email Changed', data.message);
        onEmailChange(newEmail);
        setNewEmail('');
        setConfirmEmail('');
      } else {
        toastService.error('Change Failed', data.message);
      }
    } catch (error) {
      toastService.networkError('Request Failed', 'Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const response = await fetch(`${API_URL}/create_portal_session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: state.username })
      });
      const data = await response.json();
      if (data.success) {
        window.location.href = data.result.portal_url;
      } else {
        toastService.error('Error', data.message);
        setManagingSubscription(false);
      }
    } catch (error) {
      toastService.networkError('Error', 'Please try again.');
      setManagingSubscription(false);
    }
  };

  const handleUpgrade = async () => {
    setManagingSubscription(true);
    try {
      const response = await fetch(`${API_URL}/create_checkout_session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: state.username })
      });
      const data = await response.json();
      if (data.success) {
        window.location.href = data.result.checkout_url;
      } else {
        toastService.error('Error', data.message);
        setManagingSubscription(false);
      }
    } catch (error) {
      toastService.networkError('Error', 'Please try again.');
      setManagingSubscription(false);
    }
  };

  const usageStats = state.usageStats || null;
  const isPaid = usageStats && usageStats.tier === 'paid' && usageStats.subscription_status === 'active';

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent bg="white">
            <DrawerCloseButton color="gray.700" />
            <DrawerBody>
              <ModernSidebar
                state={state}
                onRouteChange={onRouteChange}
                getOverview={getOverview}
                onRandomChapter={onRandomChapter}
                onClose={onClose}
                toggleSidebar={undefined}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Flex>
        {/* Desktop Sidebar */}
        {!isMobile && !state.sidebarCollapsed && (
          <Box
            w="280px"
            bg="white"
            minH="100vh"
            position="fixed"
            left={0}
            top={0}
            zIndex={10}
            transition="all 0.3s ease"
            borderRight="1px"
            borderColor="gray.200"
            boxShadow="sm"
          >
            <ModernSidebar
              state={state}
              onRouteChange={onRouteChange}
              getOverview={getOverview}
              onRandomChapter={onRandomChapter}
              toggleSidebar={toggleSidebar}
            />
          </Box>
        )}

        {/* Main Content */}
        <Box flex={1} ml={isMobile ? 0 : state.sidebarCollapsed ? 0 : "280px"} transition="margin-left 0.3s ease">
          {/* Top Nav Bar */}
          <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 4, md: 6 }} py={{ base: 3, md: 3 }}>
            <Flex align="center" justify="space-between">
              <HStack spacing={3}>
                {isMobile && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={onOpen}
                    variant="ghost"
                    colorScheme="brand"
                    size="sm"
                  />
                )}
                {!isMobile && state.sidebarCollapsed && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={toggleSidebar}
                    variant="ghost"
                    color="brand.600"
                    size="sm"
                    _hover={{ color: 'brand.700', bg: 'brand.50' }}
                  />
                )}
                <Heading size="md" color="gray.800">
                  Account Settings
                </Heading>
              </HStack>
              <UserMenu onRouteChange={onRouteChange} username={state.username} userStats={state.userStats} />
            </Flex>
          </Box>

          <Container maxW="container.md" py={8}>
            <VStack spacing={6} align="stretch">

              {/* Account Info Card */}
              <Card>
                <CardHeader>
                  <Heading size="md">Account Information</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>Username</FormLabel>
                      <Input value={state.username} isReadOnly bg="gray.50" />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input value={state.email || 'Not available'} isReadOnly bg="gray.50" />
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>

              {/* Subscription Card */}
              {usageStats && (
                <Card>
                  <CardHeader>
                    <Heading size="md">Subscription</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <Text>Current Plan:</Text>
                        <Badge colorScheme={isPaid ? 'purple' : 'gray'} fontSize="md" px={3} py={1}>
                          {isPaid ? 'Pro' : 'Free'}
                        </Badge>
                      </HStack>

                      {isPaid && usageStats.trial_end_date && !usageStats.cancel_at_period_end && (
                        <Text fontSize="sm" color="purple.600">
                          Free trial until {new Date(usageStats.trial_end_date).toLocaleDateString()}
                        </Text>
                      )}

                      {usageStats.cancel_at_period_end && usageStats.current_period_end && (
                        <Text fontSize="sm" color="orange.600" fontWeight="bold">
                          Your subscription will end on {new Date(usageStats.current_period_end).toLocaleDateString()}
                        </Text>
                      )}

                      {isPaid ? (
                        <Button
                          variant="outline"
                          onClick={handleManageSubscription}
                          isLoading={managingSubscription}
                        >
                          Manage Subscription
                        </Button>
                      ) : (
                        <Button
                          colorScheme="purple"
                          onClick={handleUpgrade}
                          isLoading={managingSubscription}
                        >
                          Upgrade to Pro - $9.99/month
                        </Button>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* Change Email Card */}
              <Card>
                <CardHeader>
                  <Heading size="md">Change Email</Heading>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Current email: {state.email || 'Not set'}
                  </Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4}>
                    <FormControl>
                      <FormLabel>New Email</FormLabel>
                      <InputGroup>
                        <Icon as={EmailIcon} color="gray.400" boxSize={5} mt={3} ml={3} position="absolute" zIndex={1} />
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          pl={10}
                        />
                      </InputGroup>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Confirm New Email</FormLabel>
                      <InputGroup>
                        <Icon as={EmailIcon} color="gray.400" boxSize={5} mt={3} ml={3} position="absolute" zIndex={1} />
                        <Input
                          type="email"
                          placeholder="Re-enter new email"
                          value={confirmEmail}
                          onChange={(e) => setConfirmEmail(e.target.value)}
                          pl={10}
                        />
                      </InputGroup>
                    </FormControl>

                    <Button
                      colorScheme="brand"
                      w="full"
                      onClick={handleChangeEmail}
                      isLoading={emailLoading}
                    >
                      Change Email
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              {/* Change Password Card */}
              <Card>
                <CardHeader>
                  <Heading size="md">Change Password</Heading>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Update your password to keep your account secure
                  </Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4}>
                    <FormControl>
                      <FormLabel>Current Password</FormLabel>
                      <InputGroup>
                        <Icon as={LockIcon} color="gray.400" boxSize={5} mt={3} ml={3} position="absolute" zIndex={1} />
                        <Input
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="Enter current password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          pl={10}
                        />
                        <InputRightElement>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPasswords(!showPasswords)}
                            _hover={{ bg: 'transparent' }}
                            _active={{ bg: 'transparent' }}
                            _focus={{ boxShadow: 'none' }}
                            color="gray.500"
                            _focusVisible={{ boxShadow: 'none' }}
                          >
                            <Icon as={showPasswords ? ViewOffIcon : ViewIcon} />
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <Divider />

                    <FormControl>
                      <FormLabel>New Password</FormLabel>
                      <InputGroup>
                        <Icon as={LockIcon} color="gray.400" boxSize={5} mt={3} ml={3} position="absolute" zIndex={1} />
                        <Input
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="At least 8 characters"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          pl={10}
                        />
                      </InputGroup>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Confirm New Password</FormLabel>
                      <InputGroup>
                        <Icon as={LockIcon} color="gray.400" boxSize={5} mt={3} ml={3} position="absolute" zIndex={1} />
                        <Input
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="Re-enter new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          pl={10}
                        />
                      </InputGroup>
                    </FormControl>

                    <Button
                      colorScheme="brand"
                      w="full"
                      onClick={handleChangePassword}
                      isLoading={loading}
                    >
                      Change Password
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </Container>
        </Box>
      </Flex>
    </Box>
  );
};

export default Settings;
